import * as Sentry from '@sentry/vue'

/**
 * Say why an ad did not play.
 *
 * VideoBox reports the start from mounted() unconditionally and the end only when the
 * video reaches `ended`. Every failure path — a source that errors, an autoplay the
 * browser refuses, the playback watchdog giving up after PLAYBACK_WATCHDOG_MS — calls
 * emitFinished() and returns without a word. On the server the two outcomes are
 * indistinguishable: a launch that played and a launch that never did both look like a
 * start with no end, and the layer is on the streamer's machine, so its console reaches
 * nobody.
 *
 * Measured on twitch.tv/redpandash on 03.09.2026: 26 launches in one evening, 11 reports
 * of an ad finishing. The other 15 went out through one of these paths and left nothing
 * to say which, or whether the creative was slow, broken, or refused.
 *
 * Two destinations, because they answer different questions. Sentry groups by reason
 * and carries the detail a developer reads. `fail_url` is a signed route on the
 * monolith (APiq!657) that lands the same facts in the log beside the launch they
 * belong to, where they can be counted per streamer and per task — which is what a
 * community-manager indicator would read. A payload without that field is a backend
 * older than it, and the post is skipped.
 *
 * Nothing here may throw: a failure to report a failure must not also break the layer.
 */

/** The streamer this layer belongs to, from the route (`/video/:userlink`). */
function screenFromRoute () {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean)
    return parts.length ? parts[parts.length - 1] : null
  } catch (e) {
    return null
  }
}

/**
 * Send a report to one of the layer's signed routes.
 *
 * POST because the url is signed and a signed url cannot carry an extra query
 * parameter; the signature covers the address, the body carries what happened.
 * keepalive, because a failure is often sent while the component is being torn down.
 */
function post (url, body, label) {
  if (!url) return

  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true
    }).catch((e) => console.warn(`${label} failed`, e))
  } catch (e) {
    console.warn(`${label} could not be sent`, e)
  }
}

/**
 * Acknowledge a launch broadcast the moment it arrives, before the layer decides
 * anything about it.
 *
 * This is the one thing the failure reports cannot say. They describe what became of an
 * event that arrived; a launch that never reached the machine leaves exactly the same
 * silence as one dropped by a path nobody instrumented. Measured on
 * twitch.tv/redpandash on 05.09.2026: five layer starts against seven launches, and not
 * one drop reported — with a receipt, that becomes an answer instead of a guess.
 *
 * The skew is the layer's clock against the `server_now` it was sent. The layer waits a
 * duration rather than trusting its clock, so skew no longer delays an ad; it is
 * recorded because it is still the first thing to check when one starts late.
 */
export function reportAdReceived (e, via = 'ws') {
  if (!e || !e.received_url) return

  const skew = e.server_now ? Date.now() - e.server_now * 1000 : null
  const waitMs = e.execute_at && e.server_now
    ? e.execute_at * 1000 - e.server_now * 1000
    : null

  post(e.received_url, {
    type: e.type || null,
    wait_ms: waitMs === null ? null : Math.round(waitMs),
    clock_skew_ms: skew === null ? null : Math.round(skew),
    // Which path delivered it: the broadcast, or the poll that recovers what it drops.
    via
  }, 'ad receipt')
}

function postFailure (failUrl, detail) {
  if (!failUrl) return

  post(failUrl, {
    reason: detail.reason,
    content_url: detail.contentUrl,
    ms_since_mount: detail.msSinceMount,
    wait_ms: detail.waitMs ?? null,
    ready_state: detail.readyState ?? null,
    network_state: detail.networkState ?? null,
    error_code: detail.errorCode ?? null,
    buffered_seconds: detail.bufferedSeconds ?? null
  }, 'ad failure report')
}

/**
 * @param {string} reason         'video_error' | 'watchdog' | 'play_rejected'
 * @param {Object} [context]
 * @param {string} [context.failUrl] signed route on the monolith; falsy is a no-op
 * @param {number} [context.waitMs] how far ahead the launch was scheduled, when that
 *                                  is why it was dropped
 * @param {number} [context.taskId]
 * @param {string} [context.contentUrl]
 * @param {HTMLMediaElement} [context.video]
 * @param {number} [context.startedAt] ms timestamp of mount, for how long it hung
 */
export function reportAdFailure (reason, context = {}) {
  const {
    failUrl = '', taskId = null, contentUrl = null, video = null, startedAt = null,
    waitMs = null
  } = context

  const detail = {
    reason,
    screen: screenFromRoute(),
    taskId,
    contentUrl,
    msSinceMount: startedAt ? Date.now() - startedAt : null,
    waitMs
  }

  if (video) {
    try {
      // The three numbers that separate the cases: a creative still downloading
      // (readyState 0-2, networkState 2), one whose source failed outright
      // (networkState 3, an error code), and one that loaded and was refused
      // (readyState 4 with nothing played).
      detail.readyState = video.readyState
      detail.networkState = video.networkState
      detail.currentSrc = video.currentSrc || null
      detail.errorCode = video.error ? video.error.code : null
      detail.bufferedSeconds = video.buffered && video.buffered.length
        ? Math.round(video.buffered.end(video.buffered.length - 1) * 100) / 100
        : 0
      detail.currentTime = Math.round(video.currentTime * 100) / 100
    } catch (e) {
      detail.videoStateError = String(e)
    }
  }

  console.warn('ad did not play', detail)

  postFailure(failUrl, detail)

  try {
    Sentry.captureMessage(`ad did not play: ${reason}`, {
      level: 'warning',
      // Grouped by reason, not by streamer: the question is which failure dominates.
      // The streamer is a tag so it can still be filtered and counted.
      fingerprint: ['ad-did-not-play', reason],
      tags: {
        ad_failure: reason,
        screen: detail.screen || 'unknown',
        task: taskId === null ? 'unknown' : String(taskId)
      },
      extra: detail
    })
  } catch (e) {
    // Sentry is not initialised when VUE_APP_ENVIRONMENT is local, and a transport
    // that is down is not a reason to take the layer with it.
    void e
  }
}

export default reportAdFailure
